import React, { useState } from 'react';
import '../styles/ReceiptGenerator.css';
import { ReceiptItem, ReceiptData } from '../types';

const defaultReceiptData: ReceiptData = {
  number: `RCPT-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 9999)).padStart(4, '0')}`,
  date: new Date().toISOString().split('T')[0],
  billedTo: '',
  contact: '',
  items: [{ name: 'Reading Room Monthly Fee', qty: 1, unit: 500.00 }],
  discount: 0.00,
  taxPercent: 0,
  notes: 'Thank you for your reading room membership! Monthly fees are non-refundable. Access card must be presented for entry. For any queries contact us at +91 94057 03380.'
};

const ReceiptGenerator: React.FC = () => {
  const [receiptData, setReceiptData] = useState<ReceiptData>(defaultReceiptData);
  const [editingItem, setEditingItem] = useState<number | null>(null);
  const [newItem, setNewItem] = useState<ReceiptItem>({ name: '', qty: 1, unit: 0 });

  const formatMoney = (n: number): string => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(n);
  };

  const escapeHtml = (str: string): string => {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };

  const calculateSubtotal = (): number => {
    return receiptData.items.reduce((sum, item) => sum + (item.qty * item.unit), 0);
  };

  const calculateTax = (): number => {
    const taxable = Math.max(0, calculateSubtotal() - (receiptData.discount || 0));
    return taxable * (receiptData.taxPercent / 100);
  };

  const calculateGrandTotal = (): number => {
    const subtotal = calculateSubtotal();
    const discount = receiptData.discount || 0;
    const taxable = Math.max(0, subtotal - discount);
    const tax = calculateTax();
    return taxable + tax;
  };

  const handleAddItem = () => {
    if (newItem.name.trim()) {
      setReceiptData(prev => ({
        ...prev,
        items: [...prev.items, { ...newItem }]
      }));
      setNewItem({ name: '', qty: 1, unit: 0 });
    }
  };

  const handleUpdateItem = (index: number, field: keyof ReceiptItem, value: string | number) => {
    setReceiptData(prev => ({
      ...prev,
      items: prev.items.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const handleRemoveItem = (index: number) => {
    setReceiptData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleUpdateField = (field: keyof ReceiptData, value: string | number) => {
    setReceiptData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const downloadAsHtml = () => {
    const receiptHtml = generateReceiptHtml();
    const fullHtml = `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width,initial-scale=1" />
  <title>Receipt</title>
  <style>
    ${getReceiptStyles()}
  </style>
</head>
<body>
  ${receiptHtml}
</body>
</html>`;

    const blob = new Blob([fullHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${receiptData.number || 'receipt'}.html`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const generateReceiptHtml = (): string => {
    const subtotal = calculateSubtotal();
    const discount = receiptData.discount || 0;
    const tax = calculateTax();
    const grandTotal = calculateGrandTotal();
    const date = new Date(receiptData.date);
    const formattedDate = date.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });

    const itemsHtml = receiptData.items.map(item => {
      const total = item.qty * item.unit;
      return `
        <tr>
          <td>${escapeHtml(item.name)}</td>
          <td class="qty">${item.qty}</td>
          <td class="price">${formatMoney(item.unit)}</td>
          <td class="total">${formatMoney(total)}</td>
        </tr>`;
    }).join('');

    return `
      <div class="container">
        <div class="receipt-card" id="receipt">
          <header>
            <div class="brand">
              <div class="logo" aria-hidden="true">OS</div>
              <div class="company">
                <div class="name">Om Sai Reading Room</div>
                <div class="meta">Plot No. 20, Maha Vishnu Nagar, Narsala Road, Near Gattu Factory, Nagpur – 440034</div>
              </div>
            </div>
          </header>

          <div class="billing-section">
            <div class="meta-blocks">
              <div class="meta">
                <small>Receipt No.</small>
                <div class="val">${escapeHtml(receiptData.number)}</div>
                <small style="margin-top:6px">Date</small>
                <div class="val">${formattedDate}</div>
              </div>

              <div class="meta">
                <small>Billed To</small>
                <div class="val">${escapeHtml(receiptData.billedTo)}</div>
                <small style="margin-top:6px">Phone / Email</small>
                <div class="val">${escapeHtml(receiptData.contact)}</div>
              </div>
            </div>
          </div>

          <main>
            <table class="items" id="items-table">
              <thead>
                <tr>
                  <th style="width:60%;">Item</th>
                  <th class="qty">Qty</th>
                  <th class="price">Unit Price</th>
                  <th class="total">Total</th>
                </tr>
              </thead>
              <tbody>
                ${itemsHtml}
              </tbody>
              <tfoot>
                <tr>
                  <td colspan="3" style="text-align:right;padding-top:16px;">Subtotal</td>
                  <td style="padding-top:16px;text-align:right;">${formatMoney(subtotal)}</td>
                </tr>
              </tfoot>
            </table>

            <div class="summary">
              <table>
                <tbody>
                  <tr>
                    <td>Discount</td>
                    <td style="text-align:right">${formatMoney(discount)}</td>
                  </tr>
                  <tr>
                    <td>GST (${receiptData.taxPercent}%)</td>
                    <td style="text-align:right">${formatMoney(tax)}</td>
                  </tr>
                  <tr class="total">
                    <td>Grand Total</td>
                    <td style="text-align:right">${formatMoney(grandTotal)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="notes">
              ${escapeHtml(receiptData.notes || '')}
            </div>
          </main>
        </div>
      </div>`;
  };

  const getReceiptStyles = (): string => {
    return `
      :root{
        --bg: #f8fafc;
        --card:#ffffff;
        --muted:#64748b;
        --accent:#1e293b;
        --primary:#2563eb;
        --secondary:#0891b2;
        --success:#059669;
        --danger:#dc2626;
        --warning:#d97706;
        font-family: 'Inter', 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif;
      }

      html,body{height:100%;margin:0;background:var(--bg);color:var(--accent);-webkit-font-smoothing:antialiased;font-weight:400;line-height:1.6;}
      .container{max-width:950px;margin:20px auto;padding:20px;min-height:100vh;display:flex;align-items:center;justify-content:center;}
      .receipt-card{background:var(--card);border-radius:20px;padding:40px;box-shadow:0 10px 25px rgba(0,0,0,0.08), 0 4px 6px rgba(0,0,0,0.05);border:1px solid rgba(226,232,240,0.5);position:relative;overflow:hidden;}
      
      header{display:flex;align-items:center;justify-content:flex-start;gap:20px;margin-bottom:20px;padding-top:10px;}
      .brand{display:flex;gap:16px;align-items:center;}
      .logo{
        width:80px;height:80px;border-radius:16px;background:linear-gradient(135deg,var(--primary),var(--secondary));
        display:flex;align-items:center;justify-content:center;color:white;font-weight:800;font-size:24px;
        box-shadow:0 8px 20px rgba(79,70,229,0.3);position:relative;
      }
      .logo::after{content:'';position:absolute;inset:-2px;border-radius:18px;background:linear-gradient(135deg,var(--primary),var(--secondary));z-index:-1;opacity:0.2;filter:blur(8px);}
      .company{
        line-height:1;
      }
      .company .name{font-weight:800;font-size:24px;background:linear-gradient(135deg,var(--primary),var(--secondary));-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;margin-bottom:4px;}
      .company .meta{font-size:14px;color:var(--muted);font-weight:500;opacity:0.8;}

      .billing-section{margin-bottom:25px;padding:20px 0;border-top:1px solid #e2e8f0;border-bottom:1px solid #e2e8f0;}
      .meta-blocks{display:flex;gap:30px;flex-wrap:wrap;justify-content:flex-start;}
      .meta{background:linear-gradient(135deg,#f8fafc,#f1f5f9);padding:20px;border-radius:12px;min-width:200px;border:1px solid rgba(226,232,240,0.8);box-shadow:0 4px 12px rgba(0,0,0,0.08);transition:transform 0.2s ease;}
      .meta small{display:block;color:var(--muted);font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:6px;}
      .meta .val{font-weight:700;margin-top:6px;font-size:16px;color:var(--accent);}

      main{margin-top:8px;}
      .items{width:100%;border-collapse:collapse;margin-top:18px;}
      .items th, .items td{padding:16px 12px;text-align:left;border-bottom:1px solid #eef2f7;font-size:15px;}
      .items th{font-size:13px;color:var(--muted);font-weight:700;background:linear-gradient(135deg,#f8fafc,#f1f5f9);text-transform:uppercase;letter-spacing:0.5px;padding:16px 12px;border-bottom:2px solid var(--primary);}
      .items td.qty, .items th.qty{text-align:center;width:80px;}
      .items td.price, .items td.total{text-align:right;width:120px;}
      .items tfoot td{border-bottom:0;padding-top:20px;font-weight:800;font-size:16px;color:var(--accent);}

      .summary{margin-top:18px;display:flex;justify-content:flex-end;}
      .summary table{width:360px;border-collapse:collapse;}
      .summary td{padding:8px 10px;font-size:14px;}
      .summary tr.total td{font-size:20px;font-weight:900;border-top:3px solid var(--primary);padding-top:16px;color:var(--accent);background:linear-gradient(135deg,#f8fafc,#f1f5f9);}

      .notes{margin-top:24px;padding:20px;background:linear-gradient(135deg,#f0f9ff,#e0f2fe);border-radius:12px;color:var(--muted);font-size:14px;border-left:4px solid var(--secondary);font-weight:500;line-height:1.6;}

      @media (max-width:720px){
        header{flex-direction:column;align-items:flex-start;}
        .meta-blocks{width:100%;display:grid;grid-template-columns:1fr 1fr;gap:8px;}
        .summary table{width:100%;}
      }

      @media print{
        *{color-adjust:exact;-webkit-print-color-adjust:exact;}
        body{background:white;color:black;font-size:10px;line-height:1.2;margin:0;padding:0;}
        .container{margin:0;padding:0;max-width:none;min-height:auto;display:block;height:50vh;overflow:visible;}
        .receipt-card{box-shadow:none;border-radius:0;padding:10px;border:2px solid #000;background:white;margin:0;width:100%;height:auto;position:relative;box-sizing:border-box;margin-bottom:20px;}
        .receipt-card::after{content:'';position:absolute;bottom:-15px;left:0;right:0;height:1px;background:repeating-linear-gradient(to right, #000 0px, #000 10px, transparent 10px, transparent 20px);}
        .receipt-card::before{content:'CUT HERE';position:absolute;bottom:-25px;left:50%;transform:translateX(-50%);font-size:8px;font-weight:bold;color:#000;background:white;padding:0 5px;}
        .logo{background:#000 !important;color:white !important;width:40px !important;height:40px !important;font-size:14px !important;border-radius:0 !important;}
        .company .name{font-size:16px !important;color:#000 !important;background:none !important;-webkit-background-clip:unset !important;-webkit-text-fill-color:unset !important;}
        .company .meta{font-size:9px !important;}
        .meta{background:#f5f5f5 !important;border:1px solid #ccc !important;padding:5px !important;min-width:90px !important;border-radius:0 !important;box-shadow:none !important;}
        .meta small{font-size:8px !important;text-transform:uppercase !important;}
        .meta .val{font-size:10px !important;font-weight:bold !important;}
        .billing-section{border-top:1px solid #000;border-bottom:1px solid #000;margin:8px 0;padding:8px 0;}
        .items th{background:#f0f0f0 !important;border:1px solid #000 !important;font-size:9px !important;padding:4px !important;text-transform:uppercase !important;}
        .items td{border-bottom:1px solid #ccc !important;font-size:9px !important;padding:4px !important;}
        .summary td{font-size:9px !important;padding:4px !important;}
        .summary tr.total td{border-top:2px solid #000 !important;background:#f0f0f0 !important;font-size:11px !important;font-weight:bold !important;}
        .notes{border-left:2px solid #000 !important;background:#f9f9f9 !important;font-size:8px !important;padding:5px !important;margin-top:8px !important;border-radius:0 !important;}
        header{margin-bottom:8px;padding-bottom:4px;}
        .brand{gap:8px;}
        .items{width:100%;margin-top:10px;}
        .summary table{width:100%;}
        .summary{margin-top:8px;}
        .meta-blocks{gap:12px !important;}
        .items tfoot td{padding-top:5px !important;font-size:10px !important;}
        @page{margin:0.5in;size:8.5in 11in;}
      }
    `;
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <!doctype html>
        <html>
          <head>
            <title>Receipt - ${receiptData.number}</title>
            <style>${getReceiptStyles()}</style>
          </head>
          <body>${generateReceiptHtml()}</body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 250);
    }
  };

  return (
    <div className="receipt-generator">
      <div className="receipt-generator-header">
        <h1>Receipt Generator</h1>
        <p>Create and manage receipts for reading room memberships</p>
      </div>

      <div className="receipt-generator-container">
        <div className="receipt-form-panel">
          <div className="form-section">
            <h3>Receipt Information</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Receipt Number</label>
                <input
                  type="text"
                  value={receiptData.number}
                  onChange={(e) => handleUpdateField('number', e.target.value)}
                  placeholder="RCPT-2025-0001"
                />
              </div>
              <div className="form-group">
                <label>Date</label>
                <input
                  type="date"
                  value={receiptData.date}
                  onChange={(e) => handleUpdateField('date', e.target.value)}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Billed To</label>
                <input
                  type="text"
                  value={receiptData.billedTo}
                  onChange={(e) => handleUpdateField('billedTo', e.target.value)}
                  placeholder="Customer Name"
                />
              </div>
              <div className="form-group">
                <label>Phone / Email</label>
                <input
                  type="text"
                  value={receiptData.contact}
                  onChange={(e) => handleUpdateField('contact', e.target.value)}
                  placeholder="Phone / Email"
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Items</h3>
            <div className="items-list">
              {receiptData.items.map((item, index) => (
                <div key={index} className="item-row">
                  {editingItem === index ? (
                    <>
                      <input
                        type="text"
                        value={item.name}
                        onChange={(e) => handleUpdateItem(index, 'name', e.target.value)}
                        placeholder="Item name"
                        className="item-name-input"
                      />
                      <input
                        type="number"
                        value={item.qty}
                        onChange={(e) => handleUpdateItem(index, 'qty', parseFloat(e.target.value) || 0)}
                        className="item-qty-input"
                        min="0"
                        step="1"
                      />
                      <input
                        type="number"
                        value={item.unit}
                        onChange={(e) => handleUpdateItem(index, 'unit', parseFloat(e.target.value) || 0)}
                        className="item-price-input"
                        min="0"
                        step="0.01"
                      />
                      <button
                        className="btn-icon"
                        onClick={() => setEditingItem(null)}
                        title="Save"
                      >
                        ✓
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="item-name">{item.name}</span>
                      <span className="item-qty">Qty: {item.qty}</span>
                      <span className="item-price">{formatMoney(item.unit)}</span>
                      <span className="item-total">{formatMoney(item.qty * item.unit)}</span>
                      <button
                        className="btn-icon"
                        onClick={() => setEditingItem(index)}
                        title="Edit"
                      >
                        ✎
                      </button>
                      <button
                        className="btn-icon danger"
                        onClick={() => handleRemoveItem(index)}
                        title="Remove"
                      >
                        ×
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
            <div className="add-item-form">
              <input
                type="text"
                value={newItem.name}
                onChange={(e) => setNewItem(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Item name"
                className="item-name-input"
              />
              <input
                type="number"
                value={newItem.qty}
                onChange={(e) => setNewItem(prev => ({ ...prev, qty: parseFloat(e.target.value) || 1 }))}
                className="item-qty-input"
                min="1"
                step="1"
                placeholder="Qty"
              />
              <input
                type="number"
                value={newItem.unit}
                onChange={(e) => setNewItem(prev => ({ ...prev, unit: parseFloat(e.target.value) || 0 }))}
                className="item-price-input"
                min="0"
                step="0.01"
                placeholder="Price"
              />
              <button className="btn-primary" onClick={handleAddItem}>
                Add Item
              </button>
            </div>
          </div>

          <div className="form-section">
            <h3>Pricing</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Discount (₹)</label>
                <input
                  type="number"
                  value={receiptData.discount}
                  onChange={(e) => handleUpdateField('discount', parseFloat(e.target.value) || 0)}
                  min="0"
                  step="0.01"
                />
              </div>
              <div className="form-group">
                <label>Tax Percentage (%)</label>
                <input
                  type="number"
                  value={receiptData.taxPercent}
                  onChange={(e) => handleUpdateField('taxPercent', parseFloat(e.target.value) || 0)}
                  min="0"
                  max="100"
                  step="0.01"
                />
              </div>
            </div>
            <div className="form-group">
              <label>Notes</label>
              <textarea
                value={receiptData.notes || ''}
                onChange={(e) => handleUpdateField('notes', e.target.value)}
                rows={3}
                placeholder="Additional notes for the receipt..."
              />
            </div>
          </div>
        </div>

        <div className="receipt-preview-panel">
          <div className="receipt-preview-header">
            <h3>Preview</h3>
            <div className="preview-actions">
              <button className="btn-secondary" onClick={handlePrint}>
                Print
              </button>
              <button className="btn-secondary" onClick={downloadAsHtml}>
                Download HTML
              </button>
            </div>
          </div>
          <div className="receipt-preview-container">
            <div className="receipt-preview" dangerouslySetInnerHTML={{ __html: generateReceiptHtml() }} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReceiptGenerator;

